(() => {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  const Flip = window.Flip;
  if (!gsap || !ScrollTrigger) return;

  gsap.registerPlugin(ScrollTrigger);
  if (Flip) gsap.registerPlugin(Flip);

  const page = document.body;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const enabled = page.dataset.motionEnabled !== "false" && !reduced;
  const intensity = Number(page.dataset.motionIntensity || 70) / 100;
  const duration = (value) => Math.max(.01, value * (.72 + intensity * .48));
  const contexts = new Map();
  const targets = (value) => gsap.utils.toArray(value).filter(Boolean);
  const animateFromTo = (value, fromVars, toVars, position, timeline) => {
    const items = targets(value);
    if (!items.length) return timeline;
    return timeline
      ? timeline.fromTo(items, fromVars, toVars, position)
      : gsap.fromTo(items, fromVars, toVars);
  };
  const animateTo = (value, vars) => {
    const items = targets(value);
    return items.length ? gsap.to(items, vars) : null;
  };

  const clean = (scope) => {
    const context = contexts.get(scope);
    context?.revert();
    contexts.delete(scope);
    scope.removeAttribute("data-motion-ready");
  };

  const initReveal = (scope) => {
    const items = gsap.utils.toArray("[data-reveal]", scope).filter((item) => !item.closest("[data-hero]"));
    items.forEach((item) => {
      item.classList.add("is-visible");
      gsap.fromTo(item,
        { autoAlpha: 0, y: 28 * intensity },
        {
          autoAlpha: 1,
          y: 0,
          duration: duration(.72),
          ease: "power3.out",
          clearProps: "transform,opacity,visibility",
          scrollTrigger: { trigger: item, start: "top 88%", once: true },
        },
      );
    });
  };

  const initHero = (scope) => {
    const hero = scope.querySelector?.("[data-hero]");
    if (!hero) return;
    const copy = hero.querySelector(".hero-copy");
    copy?.classList.add("is-visible");
    const timeline = gsap.timeline({ defaults: { ease: "power3.out" } });
    animateFromTo(hero.querySelector(".hero-media"), { scale: 1.025 }, { scale: 1, duration: duration(.8) }, 0, timeline);
    animateFromTo(hero.querySelectorAll(".hero-copy .eyebrow, .hero-copy h1 span"), { yPercent: 18 }, { yPercent: 0, stagger: .035, duration: duration(.48), clearProps: "transform" }, 0, timeline);
    animateFromTo(hero.querySelector(".hero-body"), { y: 12 }, { y: 0, duration: duration(.42), clearProps: "transform" }, .06, timeline);
    animateFromTo(hero.querySelectorAll(".hero-vehicle-hook:not([hidden])"), { y: 10 }, { y: 0, duration: duration(.36), clearProps: "transform" }, .08, timeline);
    animateFromTo(hero.querySelectorAll(".hero-actions > *, .hero-confidence span"), { y: 10 }, { y: 0, stagger: .035, duration: duration(.38), clearProps: "transform" }, .1, timeline);
    animateFromTo(hero.querySelector(".hero-wheel-stage"), { scale: .96, rotation: -1.25 }, { scale: 1, rotation: 0, duration: duration(.72), clearProps: "transform" }, .02, timeline);
    animateFromTo(hero.querySelectorAll(".hero-telemetry span, .hero-finish-lab"), { x: 10 }, { x: 0, stagger: .04, duration: duration(.35), clearProps: "transform" }, .12, timeline);

    animateTo(hero.querySelector(".hero-wheel-stage"), {
      yPercent: 8 * intensity,
      rotation: 1.6 * intensity,
      ease: "none",
      scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: .8 },
    });
    animateTo(hero.querySelector(".hero-media"), {
      yPercent: 4 * intensity,
      ease: "none",
      scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: 1 },
    });
  };

  const initCards = (scope) => {
    if (!window.matchMedia("(hover:hover) and (pointer:fine)").matches) return;
    gsap.utils.toArray("[data-tilt]", scope).forEach((card) => {
      const xTo = gsap.quickTo(card, "rotationY", { duration: .42, ease: "power3.out" });
      const yTo = gsap.quickTo(card, "rotationX", { duration: .42, ease: "power3.out" });
      card.addEventListener("pointermove", (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - .5;
        const y = (event.clientY - rect.top) / rect.height - .5;
        xTo(x * 4.5 * intensity);
        yTo(y * -4.5 * intensity);
      });
      card.addEventListener("pointerleave", () => {
        xTo(0);
        yTo(0);
      });
    });
  };

  const initPathways = (scope) => {
    const paths = scope.querySelectorAll?.(".shop-pathways-grid .shop-path");
    if (!paths?.length) return;
    gsap.fromTo(paths,
      { y: 34 * intensity, autoAlpha: 0, clipPath: "inset(8% 0 0 0)" },
      {
        y: 0,
        autoAlpha: 1,
        clipPath: "inset(0% 0 0 0)",
        stagger: .09,
        duration: duration(.78),
        ease: "power3.out",
        clearProps: "transform,opacity,visibility,clipPath",
        scrollTrigger: { trigger: paths[0], start: "top 84%", once: true },
      },
    );
  };

  const initFitment = (scope) => {
    const finder = scope.querySelector?.("[data-dorimm-fitment]");
    if (!finder) return;
    const fitmentTargets = finder.querySelectorAll(".dorimm-fitment__topline, .dorimm-fitment__heading > *, .dorimm-fitment__field, .dorimm-fitment__actions, .dorimm-fitment__feedback");
    if (!fitmentTargets.length) return;
    gsap.fromTo(fitmentTargets,
      { y: 22 * intensity, autoAlpha: 0 },
      {
        y: 0,
        autoAlpha: 1,
        stagger: .045,
        duration: duration(.58),
        ease: "power3.out",
        clearProps: "transform,opacity,visibility",
        scrollTrigger: { trigger: finder, start: "top 82%", once: true },
      },
    );
  };

  const initSection = (scope) => {
    if (!scope || scope.dataset?.motionReady === "true") return;
    scope.dataset.motionReady = "true";
    if (!enabled) {
      scope.querySelectorAll?.("[data-reveal]").forEach((item) => item.classList.add("is-visible"));
      return;
    }
    const context = gsap.context(() => {
      initHero(scope);
      initReveal(scope);
      initPathways(scope);
      initFitment(scope);
      initCards(scope);
    }, scope);
    contexts.set(scope, context);
  };

  const init = (scope = document) => {
    const sections = scope.matches?.(".shopify-section") ? [scope] : [...scope.querySelectorAll?.(".shopify-section") || []];
    if (sections.length) sections.forEach(initSection);
    else initSection(scope);
    requestAnimationFrame(() => ScrollTrigger.refresh());
  };

  const flip = (targets, mutate, options = {}) => {
    if (!Flip || !enabled) {
      mutate();
      return;
    }
    const state = Flip.getState(targets);
    mutate();
    Flip.from(state, {
      duration: duration(options.duration || .55),
      ease: options.ease || "power3.inOut",
      absolute: options.absolute !== false,
      stagger: options.stagger || .025,
      onComplete: () => ScrollTrigger.refresh(),
    });
  };

  window.DorimmMotion = { init, clean, flip, refresh: () => ScrollTrigger.refresh(), enabled, reduced };
  document.documentElement.classList.toggle("has-gsap-motion", enabled);

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init(), { once: true });
  else init();

  window.addEventListener("load", () => ScrollTrigger.refresh(), { once: true });
  document.addEventListener("dorimm:motion-refresh", () => ScrollTrigger.refresh());
  document.addEventListener("dorimm:fitment-results", (event) => {
    const panel = event.detail?.panel;
    if (!panel) return;
    if (!enabled) {
      panel.style.removeProperty("opacity");
      return;
    }
    gsap.fromTo(panel, { y: 26, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: duration(.65), ease: "power3.out", clearProps: "transform,opacity,visibility", onComplete: () => ScrollTrigger.refresh() });
    const resultItems = panel.querySelectorAll(".dorimm-fitment__result");
    if (resultItems.length) gsap.fromTo(resultItems, { y: 18, autoAlpha: 0 }, { y: 0, autoAlpha: 1, stagger: .055, duration: duration(.48), ease: "power3.out", clearProps: "transform,opacity,visibility" });
  });
  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => clean(event.target));
})();
